import React, { type FC } from 'react'
import { t } from 'i18next'
import { Input, Select, Checkbox, Icon, type SelectItem, type SelectValue, Tooltip } from '@awell-health/design-system'
import type { GanttFilters, GanttData } from '@awell/ui-kit/components/Gantt/types'
import { ElementType } from './types'
import { capitalize, uniqBy, isNil } from 'lodash'

interface TimelineFiltersProps {
  data: Array<GanttData>
  appliedFilters: GanttFilters & { showTracksTriggers?: boolean }
  onFiltersChange: (filters: GanttFilters & { showTracksTriggers?: boolean }) => void
  loading?: boolean
}

// Status dictionary for translating status values
const statusDictionary = {
  ACTIVE: t('activityfeed:active'),
  DONE: t('activityfeed:done'),
  STOPPED: t('activityfeed:stopped'),
  SCHEDULED: t('activityfeed:scheduled')
}

export const TimelineFilters: FC<TimelineFiltersProps> = ({
  data,
  appliedFilters,
  onFiltersChange,
  loading = false
}) => {
  // Generate option arrays from data
  const getStatusOptions = (_data: Array<GanttData>): SelectItem[] => {
    return uniqBy(
      _data.map(element => ({
        // @ts-expect-error: We don't need to worry about the discarded status
        label: capitalize(statusDictionary[element.status]),
        value: element.status
      })),
      'label'
    )
  }

  const getStakeholderOptions = (_data: Array<GanttData>): SelectItem[] => {
    return uniqBy(
      _data
        .map(d => d.stakeholders)
        .flat()
        .filter(stakeholder => !isNil(stakeholder))
        .map(stakeholder => ({
          label: capitalize(stakeholder?.name ?? ''),
          value: stakeholder?.name ?? ''
        })),
      'value'
    )
  }

  const getActivityTypeOptions = (_data: Array<GanttData>): SelectItem[] => {
    return uniqBy(_data, ({ activity_type }) => activity_type)
      .filter(({ activity_type }) => !isNil(activity_type))
      .map(({ activity_type }) => ({
        label: capitalize(activity_type ?? ''),
        value: activity_type ?? ''
      }))
  }

  const getTypeOptions = (_data: Array<GanttData>): SelectItem[] => {
    return uniqBy(_data, ({ element_type }) => element_type).map(({ element_type }) => ({
      label: capitalize(element_type),
      value: element_type
    }))
  }

  // Check if Action type is selected to enable/disable related filters
  const isActionTypeSelected = appliedFilters.type.includes(ElementType.Action)

  // Check if Track type is selected to enable track-related filters
  const isTrackTypeSelected = appliedFilters.type.includes(ElementType.Track)

  // Event handlers
  const handleSearchChange = (value: string) => {
    onFiltersChange({
      ...appliedFilters,
      query: value
    })
  }

  const handleTypeChange = (value: SelectValue) => {
    const selectedTypes = Array.isArray(value)
      ? value.map(item => (typeof item === 'object' && 'value' in item ? item.value : item))
      : []

    onFiltersChange({
      ...appliedFilters,
      type: selectedTypes
    })
  }

  const handleStatusChange = (value: SelectValue) => {
    const selectedStatuses = Array.isArray(value)
      ? value.map(item => (typeof item === 'object' && 'value' in item ? item.value : item))
      : []

    onFiltersChange({
      ...appliedFilters,
      status: selectedStatuses
    })
  }

  const handleStakeholderChange = (value: SelectValue) => {
    const selectedStakeholders = Array.isArray(value)
      ? value.map(item => (typeof item === 'object' && 'value' in item ? item.value : item))
      : []

    onFiltersChange({
      ...appliedFilters,
      stakeholder: selectedStakeholders
    })
  }

  const handleActivityTypeChange = (value: SelectValue) => {
    const selectedActivityTypes = Array.isArray(value)
      ? value.map(item => (typeof item === 'object' && 'value' in item ? item.value : item))
      : []

    onFiltersChange({
      ...appliedFilters,
      activity_type: selectedActivityTypes
    })
  }

  const handleShowTracksTriggersChange = (checked: boolean) => {
    onFiltersChange({
      ...appliedFilters,
      showTracksTriggers: checked
    })
  }

  // Convert arrays to SelectItem[] format for the Select components
  const getSelectValue = (filterArray: string[], options: SelectItem[]): SelectItem[] => {
    return filterArray.map(value => ({
      label: options.find(opt => opt.value === value)?.label || value,
      value
    }))
  }

  const typeOptions = getTypeOptions(data)
  const statusOptions = getStatusOptions(data)
  const stakeholderOptions = getStakeholderOptions(data)
  const activityTypeOptions = getActivityTypeOptions(data)
  const tooltipText = isActionTypeSelected
    ? 'Filter by activity type'
    : 'Select "Action" type to enable activity type filter'
  return (
    <div className="flex p-4 bg-white rounded-lg">
      <div className="flex flex-wrap gap-4 items-center">
        {/* Search Input */}
        <div className="min-w-[300px]">
          <Input
            placeholder={t('timeline_search_placeholder')}
            prefixIcon={<Icon icon="RiSearchLine" />}
            value={appliedFilters.query}
            onChange={e => handleSearchChange(e.target.value)}
            disabled={loading}
          />
        </div>

        {/* Element Type Filter */}
        <div className="min-w-[200px]">
          <Select
            placeholder={t('activityfeed:element')}
            options={typeOptions}
            value={getSelectValue(appliedFilters.type, typeOptions)}
            onChange={handleTypeChange}
            isMulti={true}
            isClearable={true}
            disabled={loading}
          />
        </div>

        {/* Status Filter */}
        <div className="min-w-[200px]">
          <Select
            placeholder="Status"
            options={statusOptions}
            value={getSelectValue(appliedFilters.status, statusOptions)}
            onChange={handleStatusChange}
            isMulti={true}
            isClearable={true}
            disabled={loading}
          />
        </div>

        {/* Stakeholder Filter */}
        {/* <div className="min-w-[200px]">
          <Select
            placeholder={t('stakeholder_other')}
            options={stakeholderOptions}
            value={getSelectValue(appliedFilters.stakeholder, stakeholderOptions)}
            onChange={handleStakeholderChange}
            isMulti={true}
            isClearable={true}
            disabled={loading || !isActionTypeSelected}
          />
        </div> */}

        {/* Activity Type Filter */}
        <Tooltip datatip={tooltipText} placement="right">
          <div className="min-w-[200px]">
            <Select
              placeholder={t('activity_type')}
              options={activityTypeOptions}
              value={getSelectValue(appliedFilters.activity_type, activityTypeOptions)}
              onChange={handleActivityTypeChange}
              isMulti={true}
              isClearable={true}
              disabled={loading || !isActionTypeSelected}
            />
          </div>
        </Tooltip>

        {/* Show Tracks & Triggers Checkbox */}
        {/* <div className="flex items-center">
          <Checkbox
            label={t('track_trigger_other')}
            checked={appliedFilters.showTracksTriggers || false}
            onChange={() => handleShowTracksTriggersChange(!appliedFilters.showTracksTriggers)}
            disabled={loading || !isTrackTypeSelected}
            type="checkbox"
            inputSize="md"
          />
        </div> */}
      </div>
    </div>
  )
}
